import { useMemo, useState } from "react";
import { ESTIMATE_SCALE, describeDays } from "../../../src/shared/capacity";
import { CreateEntityButton } from "./CreateEntityButton";
import { DialogActions, Modal } from "./Modal";
import { Field, Select, TextArea, TextInput } from "./Field";
import { OUTCOME_PLACEHOLDER, TitleField, TypeField } from "./RequestFields";
import { TagPicker } from "./TagPicker";
import { OwnerPicker } from "./OwnerPicker";
import { useRoster } from "../data/RosterContext";
import { useTags } from "../data/TagsContext";
import { SOURCE_LABEL, STAFF_SOURCES } from "../data/status";
import type { Account, Priority, RequestSource, RequestType } from "../data/types";

export interface NewRequestPayload {
  title: string;
  account: string;
  type: RequestType;
  priority: Priority;
  /** The account lead; defaults to the account's CE lead. */
  accountLeadId: string;
  /** The technical owner, or null while unassigned. */
  ownerId: string | null;
  /** The customer's desired outcome, "" when not given. */
  problem: string;
  description: string;
  source: RequestSource;
  sourceLink: string;
  /** YYYY-MM-DD or null. */
  nextDate: string | null;
  tagIds: string[];
  /** Show it in the customer portal from the start. On by default. */
  portalVisible: boolean;
  /** The date the customer needs it by, YYYY-MM-DD or null. */
  neededBy: string | null;
  /** Effort in days on the estimation scale, or null while unsized. */
  estimateDays: number | null;
}

/**
 * "New request" — a button that owns its dialog and a short-lived confirmation.
 *
 * Both the dashboard and the Requests list need this control, so it lives in one
 * component rather than being duplicated. It does not persist anything yet:
 * there is no requests API, so onCreated is where a POST will go later. For now
 * it closes and shows a brief confirmation, which is exactly what the draft did.
 */
export function NewRequestButton({ accounts, lockedAccount, onCreated }: { accounts: Account[]; lockedAccount?: Account; onCreated?: (payload: NewRequestPayload) => void }) {
  return (
    <CreateEntityButton<NewRequestPayload>
      label="New request"
      buildConfirmation={(p) => `Request created on ${p.account}`}
      onResult={onCreated}
    >
      {(close, create) => <NewRequestDialog accounts={lockedAccount ? [lockedAccount] : accounts} locked={lockedAccount !== undefined} onClose={close} onCreate={create} />}
    </CreateEntityButton>
  );
}

/** `locked`: the one account given is the only choice, shown but disabled (the account page). */
function NewRequestDialog({ accounts, locked, onClose, onCreate }: { accounts: Account[]; locked: boolean; onClose: () => void; onCreate: (p: NewRequestPayload) => void }) {
  const { members } = useRoster();
  const { tags } = useTags();
  const [title, setTitle] = useState("");
  const [account, setAccount] = useState(accounts[0]?.name ?? "");
  const [type, setType] = useState<RequestType>("bug");
  const [priority, setPriority] = useState<Priority>(3);
  const [leadId, setLeadId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [problem, setProblem] = useState("");
  const [description, setDescription] = useState("");
  const [source, setSource] = useState<RequestSource>("manual");
  const [sourceLink, setSourceLink] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [estimateDays, setEstimateDays] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [portalVisible, setPortalVisible] = useState(true);
  const [touched, setTouched] = useState(false);

  // Default the account lead to the account's CE lead, but let it be overridden.
  // The technical owner starts unassigned: that is a triage decision.
  const suggestedLead = useMemo(() => accounts.find((a) => a.name === account)?.lead, [accounts, account]);
  const effectiveLead = leadId || suggestedLead?.id || "";
  const titleMissing = title.trim() === "";

  function submit() {
    if (titleMissing) {
      setTouched(true);
      return;
    }
    onCreate({
      title: title.trim(),
      account,
      type,
      priority,
      accountLeadId: effectiveLead,
      ownerId: ownerId || null,
      problem: problem.trim(),
      description: description.trim(),
      source,
      sourceLink: sourceLink.trim(),
      nextDate: nextDate || null,
      tagIds,
      portalVisible,
      neededBy: neededBy || null,
      estimateDays: estimateDays ? Number(estimateDays) : null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New request"
      footer={<DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Create request" />}
    >
      <TitleField id="nr-title" value={title} onChange={setTitle} touched={touched} onBlur={() => setTouched(true)} placeholder="What the customer needs, in their words" />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Account" htmlFor="nr-account">
          <Select
            id="nr-account"
            value={account}
            disabled={locked}
            title={locked ? `Requests opened here belong to ${account}` : undefined}
            className="disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-[var(--text-2)]"
            onChange={(e) => {
              setAccount(e.target.value);
              setLeadId("");
            }}
          >
            {accounts.map((a) => (
              <option key={a.id}>{a.name}</option>
            ))}
          </Select>
        </Field>

        <TypeField id="nr-type" value={type} onChange={setType} />

        <Field label="Priority" htmlFor="nr-priority">
          <Select id="nr-priority" value={priority} onChange={(e) => setPriority(Number(e.target.value) as Priority)}>
            {[0, 1, 2, 3, 4].map((p) => (
              <option key={p} value={p}>
                {p === 0 ? "P0 · Urgent" : `P${p}`}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Account lead" htmlFor="nr-lead">
          <OwnerPicker
            id="nr-lead"
            ariaLabel="Account lead"
            members={members}
            value={effectiveLead}
            onChange={setLeadId}
            placeholder="Choose an account lead"
            note={(o) => (suggestedLead?.id === o.id ? "CE lead" : undefined)}
          />
        </Field>

        <Field label="Technical owner" htmlFor="nr-owner" hint="(optional)">
          <OwnerPicker id="nr-owner" ariaLabel="Technical owner" members={members} value={ownerId} onChange={setOwnerId} unassignedLabel="Unassigned" />
        </Field>

        <Field label="Desired outcome" htmlFor="nr-problem" hint="(optional — the problem, before any solution)" span2>
          <TextArea id="nr-problem" value={problem} onChange={(e) => setProblem(e.target.value)} placeholder={OUTCOME_PLACEHOLDER} />
        </Field>

        <Field label="Description" htmlFor="nr-desc" span2>
          <TextArea
            id="nr-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happens, what was expected, and any deal or rep names to look at"
          />
        </Field>

        <Field label="Tags" htmlFor="nr-tags" hint="(optional — leave blank for auto-tagging)" span2>
          <TagPicker id="nr-tags" tags={tags} value={tagIds} onChange={setTagIds} />
        </Field>

        <Field label="Needed by" htmlFor="nr-needed" hint="(optional)">
          <TextInput id="nr-needed" type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
        </Field>

        <Field label="Estimate" htmlFor="nr-estimate" hint="(days of work; 1 day = 1 point)">
          <Select id="nr-estimate" value={estimateDays} onChange={(e) => setEstimateDays(e.target.value)}>
            <option value="">Not yet</option>
            {ESTIMATE_SCALE.map((d) => (
              <option key={d} value={d}>
                {describeDays(d)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Commitment date" htmlFor="nr-next" hint="(optional)">
          <TextInput id="nr-next" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
        </Field>

        <Field label="Source" htmlFor="nr-source">
          <Select id="nr-source" value={source} onChange={(e) => setSource(e.target.value as RequestSource)}>
            {STAFF_SOURCES.map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex items-center sm:col-span-2">
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={portalVisible}
              onChange={(e) => setPortalVisible(e.target.checked)}
              className="h-4 w-4 accent-[var(--brand-600)]"
            />
            Visible to the customer
          </label>
        </div>

        <Field label="Source link" htmlFor="nr-src" hint="(optional)">
          <TextInput
            id="nr-src"
            value={sourceLink}
            onChange={(e) => setSourceLink(e.target.value)}
            placeholder="Slack permalink or meeting recording"
          />
        </Field>
      </div>
    </Modal>
  );
}
